// SCR-CONSOLE-022 — ثبت نرم‌افزار نزد تامین و کاتالوگ نگاشت خطا (US-INT-114، 115؛ AP-INT-10): محیط Pilot یا Production، نشانی‌های
// پایه، نشانی بازگشت ثبت‌شده (ایستا و https و در Pilot بدون پورت — BR-INT-109 ← PHX-INT-0348)، نشانی بازگشت رله مرکزی برای
// نصب‌های On-prem (BR-INT-110)، اعتبارنامه نرم‌افزار (مقدار هرگز نمایش داده نمی‌شود — BR-INT-003) و آزمون اتصال Pilot.
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type InsurerErrorMapping, type SetTaminRegistrationRequest } from '@phoenix/api-client';
import { useIdempotencyKey, usePlatformSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  DataTable,
  ErrorState,
  JalaliDate,
  ListPage,
  LoadingState,
  SampleBadge,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  type DataTableColumn,
  type SelectOption,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { intConsoleKeys } from './IntegrationsPage';

export const SCR_CONSOLE_022 = 'SCR-CONSOLE-022';

/** Adapterهای جریان احراز تامین: واقعی (سند ۱.۹.۹) و Mock فقط غیرتولید. */
const ADAPTERS: readonly SelectOption[] = [
  { value: 'INT-EPRESCRIPTION-TAMIN', labelKey: 'int:adapter.INT-EPRESCRIPTION-TAMIN' },
  { value: 'INT-EPRESCRIPTION-TAMIN-MOCK', labelKey: 'int:adapter.INT-EPRESCRIPTION-TAMIN-MOCK' },
];

const ENVIRONMENTS: readonly SelectOption[] = [
  { value: 'Pilot', labelKey: 'int:tamin.environments.Pilot' },
  { value: 'Production', labelKey: 'int:tamin.environments.Production' },
];

interface RegistrationForm {
  adapterCode: string;
  environment: string;
  accountBaseUrl: string;
  apiBaseUrl: string;
  soaBaseUrl: string;
  redirectUri: string;
  clientId: string;
  clientSecret: string;
}

export const taminConsoleKeys = {
  registration: ['integration', 'console', 'tamin-registration'] as const,
  errorMap: ['integration', 'console', 'insurer-error-map'] as const,
};

export function TaminRegistrationPage() {
  const { t } = useTranslation('int');
  const { api } = usePlatformSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();

  const registration = useQuery({
    queryKey: taminConsoleKeys.registration,
    queryFn: () => unwrap(api.GET('/api/v1/integration/console/tamin-registration')),
  });
  const errorMap = useQuery({
    queryKey: taminConsoleKeys.errorMap,
    queryFn: () => unwrap(api.GET('/api/v1/integration/console/insurer-error-map', { params: { query: {} } })),
  });

  const { control, handleSubmit, reset, setError } = useForm<RegistrationForm>({
    defaultValues: {
      adapterCode: 'INT-EPRESCRIPTION-TAMIN',
      environment: 'Pilot',
      accountBaseUrl: '',
      apiBaseUrl: '',
      soaBaseUrl: '',
      redirectUri: '',
      clientId: '',
      clientSecret: '',
    },
  });

  // پیکربندی موجود در فرم؛ رازها هرگز برنمی‌گردند، پس فیلدهای آن‌ها خالی می‌مانند (BR-INT-003).
  useEffect(() => {
    const view = registration.data;
    if (view) {
      reset({
        adapterCode: view.adapterCode,
        environment: view.environment,
        accountBaseUrl: view.accountBaseUrl ?? '',
        apiBaseUrl: view.apiBaseUrl ?? '',
        soaBaseUrl: view.soaBaseUrl ?? '',
        redirectUri: view.redirectUri ?? view.relayCallbackUri,
        clientId: '',
        clientSecret: '',
      });
    }
  }, [registration.data, reset]);

  const save = useMutation({
    mutationFn: (form: RegistrationForm) => {
      const body: SetTaminRegistrationRequest = {
        adapterCode: form.adapterCode,
        environment: form.environment,
        accountBaseUrl: form.accountBaseUrl.trim(),
        apiBaseUrl: form.apiBaseUrl.trim(),
        soaBaseUrl: form.soaBaseUrl.trim() || null,
        redirectUri: form.redirectUri.trim(),
        clientId: form.clientId.trim() || null,
        clientSecret: form.clientSecret.trim() || null,
        isActive: true,
      };
      return unwrap(api.PUT('/api/v1/integration/console/tamin-registration', { body }));
    },
    onError: (error) =>
      applyServerErrors(error, setError, {
        fields: ['redirectUri', 'accountBaseUrl', 'apiBaseUrl', 'soaBaseUrl', 'clientId', 'clientSecret'],
        codeFields: { 'PHX-INT-0348': 'redirectUri' },
      }),
    onSuccess: (view) => {
      queryClient.setQueryData(taminConsoleKeys.registration, view);
      void queryClient.invalidateQueries({ queryKey: intConsoleKeys.endpoints });
    },
  });

  const test = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/v1/integration/console/tamin-registration:test', {
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: taminConsoleKeys.registration }),
  });

  const columns: DataTableColumn<InsurerErrorMapping>[] = [
    {
      id: 'match',
      headerKey: 'int:tamin.error_map.columns.match',
      cell: (row) => row.match || t('int:tamin.error_map.any'),
    },
    {
      id: 'category',
      headerKey: 'int:tamin.error_map.columns.category',
      cell: (row) => t(`int:tamin.error_map.category.${row.category}`, { defaultValue: row.category }),
    },
    { id: 'code', headerKey: 'int:tamin.error_map.columns.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    {
      id: 'retryable',
      headerKey: 'int:tamin.error_map.columns.retryable',
      cell: (row) => (
        <StatusChip
          tone={row.isRetryable ? 'pending' : 'inactive'}
          labelKey={row.isRetryable ? 'int:tamin.error_map.retryable' : 'int:tamin.error_map.final'}
        />
      ),
    },
  ];

  if (registration.isPending) {
    return <LoadingState />;
  }

  if (registration.error || !registration.data) {
    return <ErrorState error={registration.error} onRetry={() => void registration.refetch()} />;
  }

  const view = registration.data;
  const isMock = view.adapterCode.endsWith('-MOCK');

  return (
    <ListPage screenId={SCR_CONSOLE_022} titleKey="int:tamin.title" subtitleKey="int:tamin.subtitle">
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }} component="form" onSubmit={handleSubmit((form) => save.mutate(form))}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1">{t('int:tamin.registration')}</Typography>
              {isMock ? <SampleBadge /> : null}
              <StatusChip
                tone={view.isActive ? 'active' : 'inactive'}
                labelKey={view.isActive ? 'int:tamin.active' : 'int:tamin.inactive'}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" data-testid="tamin-guide-version">
              {t('int:tamin.guide_version', { version: view.guideVersion })}
            </Typography>

            <SelectRHF control={control} name="adapterCode" labelKey="int:tamin.adapter" options={ADAPTERS} />
            <SelectRHF control={control} name="environment" labelKey="int:tamin.environment" options={ENVIRONMENTS} />
            <TextFieldRHF
              control={control}
              name="accountBaseUrl"
              labelKey="int:tamin.account_base_url"
              slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'tamin-account-base-url' } }}
            />
            <TextFieldRHF
              control={control}
              name="apiBaseUrl"
              labelKey="int:tamin.api_base_url"
              slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'tamin-api-base-url' } }}
            />
            <TextFieldRHF
              control={control}
              name="soaBaseUrl"
              labelKey="int:tamin.soa_base_url"
              slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'tamin-soa-base-url' } }}
            />
            <TextFieldRHF
              control={control}
              name="redirectUri"
              labelKey="int:tamin.redirect_uri"
              helperKey="int:tamin.redirect_uri_hint"
              slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'tamin-redirect-uri' } }}
            />
            <Alert severity="info" data-testid="tamin-relay-callback">
              {t('int:tamin.relay_hint', { uri: view.relayCallbackUri })}
            </Alert>

            <Divider />
            <Typography variant="subtitle2">{t('int:tamin.credentials')}</Typography>
            <Stack direction="row" spacing={1}>
              <span data-testid="tamin-has-client-id" data-has={String(view.hasClientId)}>
                <StatusChip
                  tone={view.hasClientId ? 'active' : 'inactive'}
                  labelKey={view.hasClientId ? 'int:tamin.client_id_set' : 'int:tamin.client_id_missing'}
                />
              </span>
              <span data-testid="tamin-has-client-secret" data-has={String(view.hasClientSecret)}>
                <StatusChip
                  tone={view.hasClientSecret ? 'active' : 'inactive'}
                  labelKey={view.hasClientSecret ? 'int:tamin.client_secret_set' : 'int:tamin.client_secret_missing'}
                />
              </span>
            </Stack>
            <TextFieldRHF
              control={control}
              name="clientId"
              labelKey="int:tamin.client_id"
              slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'tamin-client-id' } }}
            />
            <TextFieldRHF
              control={control}
              name="clientSecret"
              labelKey="int:tamin.client_secret"
              type="password"
              slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'tamin-client-secret' } }}
            />
            <Alert severity="warning">{t('int:tamin.secret_hint')}</Alert>

            {save.error ? <ErrorState error={save.error} compact /> : null}
            {test.error ? <ErrorState error={test.error} compact /> : null}
            {view.lastTestAt ? (
              <Typography variant="body2" data-testid="tamin-last-test">
                {t('int:tamin.last_test', { code: view.lastTestCode ?? '—' })}{' '}
                <JalaliDate value={view.lastTestAt} withTime />
              </Typography>
            ) : null}

            <Stack direction="row" spacing={1}>
              <Button type="submit" variant="contained" disabled={save.isPending} data-testid="tamin-save">
                {t('int:tamin.save')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<ScienceOutlinedIcon />}
                onClick={() => test.mutate()}
                disabled={test.isPending || view.endpointId === null}
                data-testid="tamin-test"
              >
                {t('int:tamin.test')}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle1">{t('int:tamin.error_map.title')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('int:tamin.error_map.subtitle')}
            </Typography>
            <DataTable
              captionKey="int:tamin.error_map.caption"
              columns={columns}
              rows={errorMap.data?.items}
              getRowId={(row) => `${row.code}-${row.match}`}
              isLoading={errorMap.isPending}
              error={errorMap.error}
              onRetry={() => void errorMap.refetch()}
              emptyTitleKey="int:tamin.error_map.empty"
            />
          </Stack>
        </Paper>
      </Stack>
    </ListPage>
  );
}
